import React, { useEffect, useMemo, useState, useCallback, useRef } from 'react';
import api from '../../utils/api';
import { toast } from 'sonner';
import { Button } from '../../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Input } from '../../components/ui/input';
import {
  ClipboardList,
  RefreshCw,
  Check,
  Loader2,
  AlertCircle,
  Lock,
  Calendar,
  Search,
  ChevronDown,
  ChevronRight,
  X,
} from 'lucide-react';

// Returns today's date in IST as YYYY-MM-DD. Uses Intl so it's correct
// regardless of the host system's timezone. Backend also enforces today on
// POST /record-closing so this is defense-in-depth.
const istTodayStr = () => {
  return new Intl.DateTimeFormat('en-CA', {
    timeZone: 'Asia/Kolkata',
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  }).format(new Date());
};

// Debounce ms for auto-save after the user stops typing on a product row.
const AUTOSAVE_DEBOUNCE_MS = 1000;

// Fixed display order for categories so the FT always sees the same layout.
// Anything unknown (Others / blank) falls through to the end.
const CATEGORY_ORDER = ['Vegetables', 'Fruits', 'Exotic', 'Sprouts', 'Flowers', 'Others'];

const categorySort = (a, b) => {
  const ai = CATEGORY_ORDER.indexOf(a);
  const bi = CATEGORY_ORDER.indexOf(b);
  if (ai === -1 && bi === -1) return a.localeCompare(b);
  if (ai === -1) return 1;
  if (bi === -1) return -1;
  return ai - bi;
};

/**
 * Field-team "Closing Inventory" tab.
 *
 * Features:
 *   - One row per product (variants collapsed) with Opening / Received /
 *     Rejection / Closing columns.
 *   - Products grouped by category (Vegetables, Fruits, Exotic, Sprouts,
 *     Flowers, Others). Each category is a collapsible section, sorted
 *     alphabetically inside.
 *   - Search bar filters product names across all categories; matching
 *     categories auto-expand while a search term is active.
 *   - Auto-save per row 1s after typing stops with inline status dot.
 *   - Date picker lets the FT browse historical closings (read-only).
 */
export const FieldTeamClosingInventoryTab = ({ retailerId, retailerName }) => {
  const today = istTodayStr();
  const [viewDate, setViewDate] = useState(today);
  const [loading, setLoading] = useState(true);
  const [products, setProducts] = useState([]);
  const [edited, setEdited] = useState({});
  // Per-row save status: 'saving' | 'saved' | 'error' | undefined (idle)
  const [saveStatus, setSaveStatus] = useState({});
  const [search, setSearch] = useState('');
  // Categories collapsed by default; search auto-expands matching ones.
  const [expanded, setExpanded] = useState({});

  const lastSavedRef = useRef({});
  const timersRef = useRef({});
  const savedBadgeTimersRef = useRef({});

  const isHistorical = viewDate !== today;

  // Clear timers on unmount / retailer / date switch.
  useEffect(() => {
    return () => {
      Object.values(timersRef.current).forEach(clearTimeout);
      Object.values(savedBadgeTimersRef.current).forEach(clearTimeout);
      timersRef.current = {};
      savedBadgeTimersRef.current = {};
    };
  }, [retailerId, viewDate]);

  const loadSummary = useCallback(async () => {
    if (!retailerId) return;
    setLoading(true);
    try {
      const res = await api.get(
        `/api/field-team/retailer/${retailerId}/closing-summary?date=${viewDate}`
      );
      const payload = res.data || {};
      const ps = payload.products || [];
      setProducts(ps);
      const seed = {};
      const seedSaved = {};
      ps.forEach((p) => {
        if (p.has_closing) {
          const s = String(p.closing_qty ?? '');
          seed[p.product_id] = s;
          seedSaved[p.product_id] = s;
        }
      });
      setEdited(seed);
      lastSavedRef.current = seedSaved;
      setSaveStatus({});
    } catch (err) {
      const msg = err?.response?.data?.detail || 'Failed to load closing summary';
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  }, [retailerId, viewDate]);

  useEffect(() => {
    loadSummary();
  }, [loadSummary]);

  const saveOne = useCallback(
    async (product, rawValue) => {
      if (isHistorical) return;
      const trimmed = (rawValue ?? '').trim();
      if (trimmed === '') {
        setSaveStatus((prev) => {
          const next = { ...prev };
          delete next[product.product_id];
          return next;
        });
        return;
      }
      const n = Number(trimmed);
      if (!isFinite(n) || n < 0) {
        setSaveStatus((prev) => ({ ...prev, [product.product_id]: 'error' }));
        toast.error(`${product.product_name}: enter a non-negative number`);
        return;
      }
      if (lastSavedRef.current[product.product_id] === String(n)) {
        setSaveStatus((prev) => ({ ...prev, [product.product_id]: 'saved' }));
        return;
      }
      setSaveStatus((prev) => ({ ...prev, [product.product_id]: 'saving' }));
      try {
        await api.post(`/api/field-team/retailer/${retailerId}/record-closing`, {
          closing_date: today,
          products: [
            {
              product_id: product.product_id,
              product_name: product.product_name,
              closing_qty: n,
            },
          ],
        });
        lastSavedRef.current[product.product_id] = String(n);
        setSaveStatus((prev) => ({ ...prev, [product.product_id]: 'saved' }));
        if (savedBadgeTimersRef.current[product.product_id]) {
          clearTimeout(savedBadgeTimersRef.current[product.product_id]);
        }
        savedBadgeTimersRef.current[product.product_id] = setTimeout(() => {
          setSaveStatus((prev) => {
            if (prev[product.product_id] !== 'saved') return prev;
            const next = { ...prev };
            delete next[product.product_id];
            return next;
          });
          delete savedBadgeTimersRef.current[product.product_id];
        }, 2000);
      } catch (err) {
        setSaveStatus((prev) => ({ ...prev, [product.product_id]: 'error' }));
        const msg = err?.response?.data?.detail || 'Save failed';
        toast.error(`${product.product_name}: ${msg}`);
      }
    },
    [retailerId, today, isHistorical]
  );

  const handleQtyChange = (product, value) => {
    if (isHistorical) return;
    const pid = product.product_id;
    setEdited((prev) => ({ ...prev, [pid]: value }));
    setSaveStatus((prev) => ({ ...prev, [pid]: 'saving' }));
    if (timersRef.current[pid]) clearTimeout(timersRef.current[pid]);
    timersRef.current[pid] = setTimeout(() => {
      delete timersRef.current[pid];
      saveOne(product, value);
    }, AUTOSAVE_DEBOUNCE_MS);
  };

  const handleBlur = (product) => {
    if (isHistorical) return;
    const pid = product.product_id;
    if (timersRef.current[pid]) {
      clearTimeout(timersRef.current[pid]);
      delete timersRef.current[pid];
      saveOne(product, edited[pid]);
    }
  };

  const searchLc = search.trim().toLowerCase();

  // Group products by category, sort each group alphabetically, filter by
  // search. If a search term is active, categories with zero matches drop
  // out entirely.
  const grouped = useMemo(() => {
    const by = {};
    products.forEach((p) => {
      if (searchLc && !(p.product_name || '').toLowerCase().includes(searchLc)) return;
      const cat = p.category || 'Others';
      if (!by[cat]) by[cat] = [];
      by[cat].push(p);
    });
    Object.values(by).forEach((arr) =>
      arr.sort((a, b) => (a.product_name || '').localeCompare(b.product_name || ''))
    );
    return Object.keys(by)
      .sort(categorySort)
      .map((cat) => ({ category: cat, items: by[cat] }));
  }, [products, searchLc]);

  // Count of products with a saved closing per category (shown on header).
  const closingCountByCategory = useMemo(() => {
    const m = {};
    products.forEach((p) => {
      const cat = p.category || 'Others';
      m[cat] = m[cat] || { total: 0, closed: 0 };
      m[cat].total += 1;
      if (p.has_closing) m[cat].closed += 1;
    });
    return m;
  }, [products]);

  // Auto-expand categories containing search matches, collapse back when
  // search clears. User can still manually toggle any section.
  useEffect(() => {
    if (!searchLc) return;
    const auto = {};
    grouped.forEach((g) => {
      auto[g.category] = true;
    });
    setExpanded((prev) => ({ ...prev, ...auto }));
  }, [searchLc, grouped]);

  const toggleCat = (cat) => {
    setExpanded((prev) => ({ ...prev, [cat]: !prev[cat] }));
  };

  const expandAll = () => {
    const all = {};
    grouped.forEach((g) => {
      all[g.category] = true;
    });
    setExpanded(all);
  };

  const collapseAll = () => setExpanded({});

  const renderStatusDot = (pid) => {
    const s = saveStatus[pid];
    if (!s) return <span className="inline-block w-4 h-4" />;
    if (s === 'saving')
      return (
        <span title="Saving..." className="inline-flex items-center">
          <Loader2 className="w-4 h-4 text-blue-500 animate-spin" />
        </span>
      );
    if (s === 'saved')
      return (
        <span title="Saved" className="inline-flex items-center">
          <Check className="w-4 h-4 text-emerald-600" />
        </span>
      );
    if (s === 'error')
      return (
        <span title="Save failed" className="inline-flex items-center">
          <AlertCircle className="w-4 h-4 text-red-600" />
        </span>
      );
    return null;
  };

  const renderProductRow = (p, idx) => {
    const hasActivity =
      (p.opening_qty || 0) > 0 || (p.received_qty || 0) > 0 || (p.rejection_qty || 0) > 0;
    return (
      <tr
        key={p.product_id}
        className={`border-b last:border-b-0 ${hasActivity ? '' : 'opacity-60'}`}
        data-testid={`ft-closing-row-${p.product_id}`}
      >
        <td className="p-2 text-gray-500 w-6">{idx + 1}</td>
        <td className="p-2 font-medium text-gray-800">
          {p.product_name}
          {p.variants?.length > 1 && (
            <span className="ml-2 text-[10px] text-gray-400">
              ({p.variants.length} packs combined)
            </span>
          )}
        </td>
        <td className="p-2 text-right text-gray-700">
          {(p.opening_qty || 0).toFixed(2)}
        </td>
        <td className="p-2 text-right text-gray-700">
          {(p.received_qty || 0).toFixed(2)}
        </td>
        <td className="p-2 text-right text-red-600">
          {(p.rejection_qty || 0).toFixed(2)}
        </td>
        <td className="p-2 text-right">
          <div className="flex items-center justify-end gap-1.5">
            {!isHistorical && (
              <span
                className="w-4 h-4 inline-flex items-center justify-center"
                data-testid={`ft-closing-status-${p.product_id}`}
              >
                {renderStatusDot(p.product_id)}
              </span>
            )}
            {isHistorical ? (
              <span
                className="text-gray-800 font-medium"
                data-testid={`ft-closing-readonly-${p.product_id}`}
              >
                {p.has_closing ? (p.closing_qty || 0).toFixed(2) : '-'}
              </span>
            ) : (
              <Input
                type="number"
                step="0.01"
                min="0"
                placeholder="0"
                className="h-8 w-24 text-sm text-right"
                value={edited[p.product_id] ?? ''}
                onChange={(e) => handleQtyChange(p, e.target.value)}
                onBlur={() => handleBlur(p)}
                data-testid={`ft-closing-input-${p.product_id}`}
              />
            )}
          </div>
        </td>
      </tr>
    );
  };

  const totalShown = grouped.reduce((s, g) => s + g.items.length, 0);

  return (
    <Card data-testid="ft-closing-tab">
      <CardHeader className="border-b py-3 px-4">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
          <CardTitle className="text-base flex items-center gap-2">
            <ClipboardList className="text-green-600" size={18} />
            Closing Inventory - {retailerName || 'Retailer'}
          </CardTitle>
          <div className="flex items-center gap-2 flex-wrap">
            <div className="flex items-center gap-1.5">
              <Calendar size={14} className="text-gray-500" />
              <Input
                type="date"
                value={viewDate}
                max={today}
                onChange={(e) => setViewDate(e.target.value || today)}
                className="h-9 text-sm w-[160px]"
                data-testid="ft-closing-date-picker"
              />
              {viewDate !== today && (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setViewDate(today)}
                  className="h-9 text-xs"
                  data-testid="ft-closing-today-btn"
                >
                  Today
                </Button>
              )}
            </div>
            <Button
              variant="outline"
              size="sm"
              onClick={loadSummary}
              disabled={loading}
              data-testid="ft-closing-refresh-btn"
            >
              <RefreshCw size={14} className={loading ? 'animate-spin' : ''} />
            </Button>
          </div>
        </div>

        {isHistorical ? (
          <div className="mt-2 flex items-center gap-2 text-xs bg-amber-50 border border-amber-200 text-amber-800 rounded px-2 py-1">
            <Lock size={12} />
            Viewing <span className="font-semibold">{viewDate}</span> - read only.
            Only today's closing can be edited.
          </div>
        ) : (
          <p className="text-xs text-gray-500 mt-2">
            Values auto-save as you type. Grouped by category; expand a section
            to record closing.
          </p>
        )}

        {/* Search + expand/collapse controls */}
        <div className="mt-3 flex items-center gap-2 flex-wrap">
          <div className="relative flex-1 min-w-[200px] max-w-md">
            <Search
              size={14}
              className="absolute left-2.5 top-1/2 -translate-y-1/2 text-gray-400"
            />
            <Input
              type="text"
              placeholder="Search product..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="h-9 text-sm pl-8 pr-8"
              data-testid="ft-closing-search-input"
            />
            {search && (
              <button
                onClick={() => setSearch('')}
                className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-700"
                data-testid="ft-closing-search-clear"
              >
                <X size={14} />
              </button>
            )}
          </div>
          <Button
            variant="outline"
            size="sm"
            onClick={expandAll}
            className="h-9 text-xs"
            data-testid="ft-closing-expand-all-btn"
          >
            Expand all
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={collapseAll}
            className="h-9 text-xs"
            data-testid="ft-closing-collapse-all-btn"
          >
            Collapse all
          </Button>
          {searchLc && (
            <span className="text-xs text-gray-500" data-testid="ft-closing-search-count">
              {totalShown} match{totalShown === 1 ? '' : 'es'}
            </span>
          )}
        </div>
      </CardHeader>

      <CardContent className="p-0">
        {loading ? (
          <div className="flex items-center justify-center py-10 text-gray-500">
            <RefreshCw className="w-5 h-5 animate-spin mr-2" />
            Loading...
          </div>
        ) : grouped.length === 0 ? (
          <div className="text-center py-10 text-gray-500 text-sm">
            {searchLc
              ? `No products matching "${search}".`
              : isHistorical
              ? 'No closing records for this date.'
              : 'No products supplied in the last 30 days.'}
          </div>
        ) : (
          <div className="divide-y">
            {grouped.map((g) => {
              const isOpen = !!expanded[g.category];
              const stats = closingCountByCategory[g.category] || { total: 0, closed: 0 };
              return (
                <div
                  key={g.category}
                  data-testid={`ft-closing-category-${g.category.toLowerCase()}`}
                >
                  <button
                    onClick={() => toggleCat(g.category)}
                    className="w-full flex items-center justify-between px-4 py-3 hover:bg-gray-50 transition-colors text-left"
                    data-testid={`ft-closing-category-toggle-${g.category.toLowerCase()}`}
                  >
                    <div className="flex items-center gap-2">
                      {isOpen ? (
                        <ChevronDown size={16} className="text-gray-500" />
                      ) : (
                        <ChevronRight size={16} className="text-gray-500" />
                      )}
                      <span className="font-semibold text-sm text-gray-800">
                        {g.category}
                      </span>
                      <span className="text-xs text-gray-500">
                        ({g.items.length})
                      </span>
                    </div>
                    {!isHistorical && !searchLc && (
                      <span
                        className={`text-xs font-medium px-2 py-0.5 rounded ${
                          stats.closed === stats.total && stats.total > 0
                            ? 'bg-emerald-100 text-emerald-700'
                            : stats.closed > 0
                            ? 'bg-amber-100 text-amber-700'
                            : 'bg-gray-100 text-gray-500'
                        }`}
                      >
                        {stats.closed}/{stats.total} closed
                      </span>
                    )}
                  </button>
                  {isOpen && (
                    <div className="overflow-x-auto bg-white">
                      <table className="w-full text-sm" data-testid={`ft-closing-table-${g.category.toLowerCase()}`}>
                        <thead className="bg-gray-50 border-y">
                          <tr>
                            <th className="p-2 text-left font-medium text-gray-600 w-6">#</th>
                            <th className="p-2 text-left font-medium text-gray-600">Product</th>
                            <th className="p-2 text-right font-medium text-gray-600">Opening</th>
                            <th className="p-2 text-right font-medium text-gray-600">Received</th>
                            <th className="p-2 text-right font-medium text-gray-600">Rejection</th>
                            <th className="p-2 text-right font-medium text-green-700">
                              Closing{!isHistorical ? ' *' : ''}
                            </th>
                          </tr>
                        </thead>
                        <tbody>{g.items.map((p, i) => renderProductRow(p, i))}</tbody>
                      </table>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default FieldTeamClosingInventoryTab;
